import { toQueryString } from '@utils/requests';

import { H3CoreSettingsResponse } from 'integrations/H3core/types';
import {
  HireflixError,
  HireflixInterview,
  HireflixJobEnabled,
  HireflixPosition,
  HireflixPositionSuccess,
  HireflixSettings,
} from 'integrations/hireflix/types';
import { OnboardingFormTemplateResponse } from 'integrations/onboardingForm/types';
import { TimonSettingsResponse } from 'integrations/timon/types';
import { IIntegration } from 'models/types';
import { ITODO } from 'types/todo';

import { request, unhandledRequest } from './request';

export async function applicationPreHireApps(applicationId: string | number) {
  const response = await unhandledRequest.get<Array<IIntegration>>({
    url: `/applications/${applicationId}/integrations/applicant_pre_hire/`,
  });
  return response.data;
}

export function getacceptAuth(baseUrl: string, code: string) {
  return request.get({
    url: `/integrations/getaccept/auth/` + toQueryString({ url: baseUrl, code }),
  });
}

export function getacceptSettings() {
  return request.get({
    url: '/integrations/getaccept/settings/',
  });
}

export function getacceptSaveSettings(data: ITODO) {
  return request.post({
    url: '/integrations/getaccept/settings/',
    data,
  });
}

export function getacceptSubmitTemplate(templateId: number, values: ITODO) {
  return request.patch({
    url: `/integrations/getaccept/templates/${templateId}/`,
    data: { mapper: values },
  });
}

export function getacceptTemplates() {
  return request.get({
    url: '/integrations/getaccept/templates/',
  });
}

export function getacceptFields(templateId: number) {
  return request.get({
    url: `/integrations/getaccept/templates/${templateId}/`,
  });
}

export async function onboardingFormGetTemplates() {
  const response = await unhandledRequest.get<OnboardingFormTemplateResponse>({
    url: '/integrations/rookie/templates/',
  });
  return response.data.templates;
}

export async function onboardingFormBulkSend(templateId: number, applicationIds: number[]) {
  const response = await unhandledRequest.post({
    url: '/integrations/rookie/bulk/',
    data: { templateId, applicationIds },
  });
  return response.data;
}

export function kjarniEmails() {
  return request.get({
    url: '/integrations/kjarni/settings/',
  });
}

export function kjarniSubmitEmail(emails: ITODO) {
  return request.patch({
    url: '/integrations/kjarni/settings/',
    data: { emails },
  });
}

export function asanaHasToken() {
  return request.get({
    url: '/integrations/asana/settings/',
  });
}

export function asanaSubmitToken(token: string) {
  return request.patch({
    url: '/integrations/asana/settings/',
    data: { token },
  });
}

export function hrMonitorAuth() {
  return request.get({
    url: '/integrations/hr_monitor/auth/',
  });
}

export function hrMonitorSetCompanyId(companyId: number) {
  return request.post({
    url: '/integrations/hr_monitor/auth/',
    data: { companyId },
  });
}

export function slackSettings() {
  return request.get({
    url: '/integrations/slack/settings/',
  });
}

export function authenticateSlack(code: string, redirectUri: string) {
  const query = toQueryString({ code, redirect_uri: redirectUri });
  return request.get({
    url: '/integrations/slack/auth/' + query,
  });
}

export function relesysSettings() {
  return request.get({
    url: '/integrations/relesys/settings/',
  });
}

export function relesysSubmitSettings(data: ITODO) {
  return request.patch({
    url: '/integrations/relesys/settings/',
    data,
  });
}

export async function hireflixPositions() {
  const response = await unhandledRequest.get<Array<HireflixPositionSuccess> | HireflixError>({
    url: '/integrations/hireflix/positions/',
  });
  return response.data;
}

export async function hireflixGetLinkedJobPosition(jobId: string) {
  const response = await unhandledRequest.get<HireflixPosition>({
    url: `/integrations/hireflix/positions/link/${jobId}/`,
  });
  return response.data;
}

export function hireflixRemoveLinkedJobPosition(jobId: string) {
  return unhandledRequest.delete({
    url: `/integrations/hireflix/positions/link/${jobId}/`,
  });
}

export function hireflixLinkJobPosition(jobId: string, positionId: string, positionName: string) {
  const data = { jobId, positionId, positionName };
  return unhandledRequest.post({
    url: '/integrations/hireflix/positions/link/',
    data,
  });
}

export async function hireflixGetInterview(applicationId: string) {
  const response = await unhandledRequest.get<HireflixInterview | HireflixError>({
    url: `/integrations/hireflix/${applicationId}/interview/`,
  });
  return response.data;
}

export function hireflixSendInterview(applicationId: string) {
  return request.post<null | HireflixError>({
    url: `/integrations/hireflix/${applicationId}/interview/`,
  });
}

export function hireflixDeleteInterview(applicationId: string) {
  return request.delete({
    url: `/integrations/hireflix/${applicationId}/interview/`,
  });
}

export function hireflixMakeFinalist(applicationId: string) {
  return request.post<null | HireflixError>({
    url: `/integrations/hireflix/${applicationId}/interview/finalist/`,
  });
}

export function hireflixDiscardInterview(applicationId: string) {
  return request.post<null | HireflixError>({
    url: `/integrations/hireflix/${applicationId}/interview/discard/`,
  });
}

export function hireflixResetInterview(applicationId: string) {
  return request.post<null | HireflixError>({
    url: `/integrations/hireflix/${applicationId}/interview/reset/`,
  });
}

export function hireflixQuestionRating(
  applicationId: string,
  questionId: string,
  rating: number | null
) {
  return request.post({
    url: `/integrations/hireflix/${applicationId}/interview/rating/`,
    data: { questionId, rating },
  });
}

export async function hireflixGetSettings() {
  const response = await unhandledRequest.get<HireflixSettings>({
    url: `/integrations/hireflix/settings/`,
  });
  return response.data;
}

export async function hireflixJobEnabled(jobId: string) {
  const response = await unhandledRequest.get<HireflixJobEnabled>({
    url: `/integrations/hireflix/positions/${jobId}/`,
  });
  return response.data;
}

export function hireflixSaveSettings(apiKey: string) {
  return unhandledRequest.post({
    url: `/integrations/hireflix/settings/`,
    data: { apiKey },
  });
}

export function hireflixBulkSendInterview(jobID: string, applications: number[]) {
  return unhandledRequest.post<void>({
    url: `/integrations/hireflix/positions/${jobID}/bulk/interview/`,
    data: { applicationIds: applications },
  });
}

export async function h3coreGetSettings() {
  const response = await unhandledRequest.get<H3CoreSettingsResponse>({
    url: `/integrations/h3core/settings/`,
  });
  return response.data;
}

export function h3coreSaveSettings(
  baseUrl: string,
  companyName: string,
  username: string,
  password: string
) {
  return unhandledRequest.patch({
    url: `/integrations/h3core/settings/`,
    data: { baseUrl, companyName, username, password },
  });
}

export async function timonGetSettings() {
  const response = await unhandledRequest.get<TimonSettingsResponse>({
    url: `/integrations/timon/settings/`,
  });
  return response.data;
}

export function timonSaveSettings(url: string, username: string, password: string) {
  return unhandledRequest.patch({
    url: `/integrations/timon/settings/`,
    data: { url, username, password },
  });
}
